import {
    select as D3select,
    local as D3local,
    scaleLinear as D3scaleLinear,
    scaleTime as D3scaleTime,
    scaleOrdinal as D3scaleOrdinal,
    axisBottom as D3axisBottom,
    bisector as D3bisector,
    extent as D3extent,
    symbol as D3symbol,
    symbolCircle as D3symbolCircle,
    format as D3format,
    interpolateRgb as D3interpolateRgb,
    zoom as D3zoom,
    zoomIdentity as D3zoomIdentity,
    dispatch as D3dispatch,
    pointer as D3pointer,
    min as D3min,
    bisectLeft as D3bisectLeft,
} from 'd3';
import { sliderBottom } from 'd3-simple-slider';
import Moment from 'moment';
import { get as _get } from 'lodash';

import D3Sparkline from './D3Sparkline';
import D3Button from './D3Button';
import { AddValueLine } from './utils';
/*
    A d3 component for a collection of timeline charts with fancy tooltip action.
    The charts can be a combination of line charts or bar charts controlled by
    setting metric.type === 'bar', default is a line chart.

    Expects: { timeline, key, metrics }
    (see code examples for what's expected in the metrics list)

    Required properties
    width: width
    x: d3 time scale with valid domain and range

    Optional properties
    parentHeight: total height of all timelines in group. leave empty to hide the vertical tooltip line
    settings: a settings object to override one or more of these defaults (see below)
    colors: array with two colors to create a gradient effect in the timeline colors from top to bottom
            if none provided will use the 'color' property in the .timelines .linechartarea, .timelines .linechart styles
    xval: a function retrieve x date value. Default is timestamp
    offset: a function to dynamically set x, y position on page relative to parent container
*/
const D3SparklineGroup = function () {
    let width = 0;
    let start;
    let end;
    let height = 0;
    let settings = {
        margin: 20,
        labelWidth: 160,
        chartHeight: 25,
        barGap: 1,
        barWidth: 5,
        scale: 'linear',
        expandHeight: 400,
        toTitle: function (s) { return s.replace('_', ' ') },
        sliderWidth: 250,
    };
    let colors = [];
    let colorScale;
    let xval = function (d) { return new Date(d.timestamp) };
    let theme;
    let expand;
    let txfm = D3zoomIdentity;
    const localY = D3local();
    const localKey = D3local();

    const dispatch = D3dispatch('labelClick', 'click', 'axis-hover', 'zoom');

    function my (selection) {
        if (!selection) return;
        selection.each(function (data, i) {
            let _txfm = D3zoomIdentity;
            if (typeof txfm === 'function') {
                _txfm = txfm(data, i);
            }
            const svg = D3select(this);

            // './' is not a valid selector
            const key = (data.key || 'dakey').split('/').join('slash');

            // add a transparent rect to capture mouse events FML (bounding-box not widely supported.)
            const mouser = D3select(this).selectAll('.mouser').data([key]);
            mouser.enter()
                .append('rect')
                .attr('class', 'mouser')
                .attr('fill', 'transparent')
                .attr('fill-opacity', 0.4)
                .style('pointer-events', 'all')
                .on('mouseleave', mouseout)
                .merge(mouser)
                .attr('width', width - settings.margin - settings.labelWidth)
                .attr('height', +svg.attr('height'))
                .attr('transform', `translate(${settings.labelWidth},0)`);

            const _expand = (expand && expand.key === data.key) ? expand.metric : undefined;
            const totalheight = settings.chartHeight * data.metrics.length +
                (_expand ? settings.expandHeight : 0);

            const xstart = settings.labelWidth;
            const xend = width - settings.margin - settings.barWidth;
            let x = D3scaleTime()
                .range([xstart, xend])
                .domain([new Date(start), new Date(end)]);

            // limit pan area
            const sliderScale = D3scaleLinear().domain([1, 42]).range([0, 300]);
            const slider = sliderBottom(sliderScale)
                .width(settings.sliderWidth)
                .ticks(0)
                .step(1)
                .default(1)
                .displayValue(false)
                .handle(D3symbol()
                    .type(D3symbolCircle)
                    .size(200)());

            const zoomExtent = [
                [xstart, 0],
                [xend, totalheight]
            ];
            const zoom = D3zoom().scaleExtent([1, 42]);
            zoom.translateExtent(zoomExtent)
                .extent(zoomExtent)
                .on('zoom', (e) => {
                    _txfm = e.transform;
                    dispatch.call('zoom', my, { txfm: e.transform, key: data.key });
                    slider.value(e.transform.k);
                });

            x = _txfm && _txfm.k > 1 ? _txfm.rescaleX(x) : x;
            // enter cool tooltips
            const bisect = D3bisector(xval).left;

            // g added only once on enter
            const g = D3select(this).selectAll('.timelines').data([key]);
            const genter = g.enter()
                .append('g')
                .attr('class', 'timelines')
                .attr('transform', 'translate(0,15)');
            const gmerge = genter.merge(g).classed(key, true);

            D3select(this).selectAll(`.timelines, .${key}`).call(zoom);

            let z;
            if (colorScale === undefined) {
                const cs = D3scaleLinear()
                    .interpolate(D3interpolateRgb)
                    .domain([0, data.metrics.length - 1]);

                z = D3scaleOrdinal();
                // if no colors passed in we'll let the stylesheet pick the color
                if (colors.length > 0) {
                    cs.range([colors[0], colors[1]]);
                    z.range(data.metrics.map(function (m, i) { return cs(i) }));
                    z.domain(data.metrics.map(function (m) { return m.key }));
                } else {
                    z.nocolors = true;
                }
            } else {
                z = typeof colorScale === 'function' ? colorScale : () => colorScale;
            }

            // clip path
            const clippath = gmerge.selectAll('defs').data(['once']);
            const clipEnter = clippath.enter()
                .append('defs')
                .append('svg:clipPath')
                .attr('id', `clip_path_${key}`);
            const clipPathMerge = clipEnter.merge(clippath);
            // clip rect added once
            const clipRect = clipPathMerge.selectAll('#clip-rect').data(['once']);
            const clipRectEnter = clipRect.enter()
                .append('svg:rect')
                .attr('id', 'clip-rect');
            clipRectEnter.merge(clipRect)
                .attr('x', xstart)
                .attr('y', 0)
                .attr('width', xend - xstart)
                .attr('height', settings.expandHeight + settings.chartHeight); // +svg.attr('height')); // '100%');

            const join = gmerge.selectAll('.timeline-metric')
                .data(data.metrics.map(function (m) {
                    return { metric: m, timeline: data.timeline, key: data.key, safekey: key };
                }), function (m) {
                    if (!m || !m.metric) {
                        return m.metric;
                    }
                    return m.metric.key;
                });

            // enter x-axis first
            genter.append('g')
                .attr('class', 'vgrid timeline-axis')
                .style('pointer-events', 'none');

            // update x-axis
            gmerge.selectAll('.timeline-axis')
                .attr('transform', function () {
                    return 'translate(0,' + (totalheight + 10) + ')';
                })
                .call(D3axisBottom(x)
                    .tickSizeInner(settings.vgrid
                        ? (-totalheight - (settings.hgrid ? 10 : 0)) : 0)
                    .tickSizeOuter(5)
                    .tickPadding(5));

            // enter slider
            genter.append('g')
                .style('pointer-events', 'all')
                .attr('class', 'scale-slider')
                .style('opacity', 0)
                .on('mouseenter', mouseenter);

            // update slider
            const sliderUpdate = gmerge.selectAll('.scale-slider')
                .attr('transform', `translate(${x.range()[1] - settings.sliderWidth - 80},  ${totalheight + 40})`)
                .call(slider
                    .on('onchange', v => {
                        zoom.scaleTo(svg, v);
                        slider.value(v);
                    }));

            // add slider styles
            sliderUpdate.selectAll('.slider');
            sliderUpdate.selectAll('.handle')
                .attr('stroke', theme.palette.primary.main)
                .attr('fill', theme.palette.primary.main);
            sliderUpdate.selectAll('line')
                .attr('stroke', theme.palette.primary.main)
                .attr('stroke-width', 2);

            // add slider buttons
            // you can grab icon paths from http://materialdesignicons.com/
            const btns = [
                {
                    key: 'in',
                    path: 'M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z',
                    x: settings.sliderWidth + 30,
                    y: -10,
                    disabled: _txfm.k >= 42,
                    handler: (elm, d, i) => zoom.scaleBy(svg, 2)
                },
                {
                    key: 'out',
                    path: 'M19,13H5V11H19V13Z',
                    x: -40,
                    y: -15,
                    disabled: _txfm.k <= 1,
                    handler: (elm, d, i) => zoom.scaleBy(svg, 0.5)
                },
                {
                    key: 'left',
                    path: 'M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z',
                    x: -70,
                    y: -10,
                    disalbed: false,
                    handler: () => zoom.translateBy(svg, 200, 0),
                },
                {
                    key: 'right',
                    path: 'M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z',
                    x: settings.sliderWidth + 60,
                    y: -10,
                    disalbed: false,
                    handler: () => zoom.translateBy(svg, -200, 0),
                }];

            const btnjoin = sliderUpdate.selectAll('.slider-button')
                .data(btns, d => d.key);
            btnjoin.enter()
                .append('g')
                .attr('class', 'slider-button')
                .merge(btnjoin)
                .call(D3Button()
                    .disabled(d => d.disabled)
                    .round(true)
                    .on('press', (elm, d, i) => {
                        d.handler(elm, d, i);
                        // e.stopPropagation();
                    }));
            btnjoin.exit().remove();

            // add horizontal grid if requested
            if (settings.hgrid) {
                const hgriddata = [...Array(data.metrics.length + 1).keys()];
                gmerge.selectAll('.hgrid')
                    .data(hgriddata)
                    .join('line')
                    .attr('class', 'hgrid')
                    .attr('stroke', 'currentColor')
                    .attr('opacity', 0.2)
                    .attr('sroke-width', 1)
                    .attr('x1', 0)
                    .attr('x2', xend - settings.labelWidth)
                    .attr('y1', d => d * settings.chartHeight)
                    .attr('y2', d => d * settings.chartHeight);
            }

            let ei = _expand ? data.metrics.findIndex(m => m.key === _expand) : undefined;
            ei = ei >= 0 ? ei : undefined;
            const getPosition = i => (i * settings.chartHeight + (i > ei ? settings.expandHeight : 0));
            const getHeight = i => (settings.chartHeight + (i === ei ? settings.expandHeight : 0));
            const positions = data.metrics.map((m, i) => getPosition(i));

            // enter + update
            const jenter = join.enter()
                .append('g')
                .attr('class', 'timeline-metric');
            jenter.merge(join)
                .attr('transform', (d, i) => `translate(0, ${positions[i]})`)
                .classed('clickable', _get(dispatch, '_.click', []).length > 0)
                .classed('expanded', (d, i) => i === ei)
                .call(D3Sparkline()
                    .theme(theme)
                    .localY(localY)
                    .localKey(localKey)
                    .color(function (d) {
                        if (z.nocolors) return undefined;
                        return z(d);
                    })
                    .settings(settings)
                    .chartHeight((i) => getHeight(i))
                    .x(x)
                    .xval(xval)
                    .on('labelClick', (e, o) => {
                        dispatch.call('labelClick', my, o);
                        e.stopPropagation();
                    }));

            // exit
            join.exit().remove();

            // hover line
            genter.append('g')
                .attr('class', 'group-hover')
                .append('path')
                .attr('class', 'hover-line')
                .style('opacity', 0)
                .style('pointer-events', 'none')
                .style('stroke-width', '0.5px');

            // tip date
            genter.append('g')
                .attr('class', 'tip-container')
                .style('pointer-events', 'none')
                .append('text')
                .attr('class', 'tip-date tooltip')
                .attr('dy', '4')
                .attr('text-anchor', 'end')
                .style('pointer-events', 'none')
                .style('white-space', 'nowrap');

            // show extra info on hover if selected
            const stats = {};
            if (_expand && ei >= 0 && ei < data.metrics.length) {
                const xzoom = _txfm.rescaleX(x);
                stats.start = xzoom.domain()[0];
                stats.end = xzoom.domain()[1];
                const istart = bisect(data.timeline, stats.start);
                const iend = bisect(data.timeline, stats.end);
                const limited = data.timeline.slice(istart, iend);
                const extent = D3extent(limited, d => data.metrics[ei].value(d) || 0);
                stats.min = extent[0];
                stats.max = extent[1];
            }
            const info = gmerge.selectAll('.selection-info')
                .data(_expand ? [_expand] : []);
            const infoEnter = info.enter()
                .append('svg')
                .attr('class', 'selection-info');
            const infoUpdate = infoEnter.merge(info)
                .style('width', settings.labelWidth - 25)
                .style('height', settings.expandHeight)
                .attr('x', 0)
                .attr('y', positions[ei]);
            info.exit().remove();

            // labels
            infoEnter.append('text')
                .attr('text-anchor', 'start')
                .attr('y', positions[ei] + 100)
                .attr('class', 'title min')
                .style('pointer-events', 'none')
                .text('Min:');
            infoEnter.append('text')
                .attr('text-anchor', 'start')
                .attr('y', positions[ei] + 125)
                .attr('class', 'title max')
                .style('pointer-events', 'none')
                .text('Max:');
            infoEnter.append('text')
                .attr('text-anchor', 'start')
                .attr('y', positions[ei] + 150)
                .attr('class', 'title start')
                .style('pointer-events', 'none')
                .text('Start:');
            infoEnter.append('text')
                .attr('text-anchor', 'start')
                .attr('y', positions[ei] + 175)
                .attr('class', 'title end')
                .style('pointer-events', 'none')
                .text('End:');

            // values:
            infoEnter.append('text')
                .attr('text-anchor', 'end')
                .attr('y', positions[ei] + 100)
                .attr('x', settings.labelWidth - 20)
                .attr('class', 'value min')
                .style('pointer-events', 'none');
            infoUpdate.selectAll('.value.min')
                .text(_expand ? `${D3format(',.2f')(stats.min)}` : '');
            infoEnter.append('text')
                .attr('text-anchor', 'end')
                .attr('y', positions[ei] + 125)
                .attr('x', settings.labelWidth - 20)
                .attr('class', 'value max')
                .style('pointer-events', 'none');
            infoUpdate.selectAll('.value.max')
                .text(_expand ? `${D3format(',.2f')(stats.max)}` : '');
            infoEnter.append('text')
                .attr('text-anchor', 'end')
                .attr('y', positions[ei] + 150)
                .attr('x', settings.labelWidth - 20)
                .attr('class', 'value start')
                .style('pointer-events', 'none');
            infoUpdate.selectAll('.value.start')
                .text(_expand ? `${Moment(stats.start).format(theme.date.short)}` : '');
            infoEnter.append('text')
                .attr('text-anchor', 'end')
                .attr('y', positions[ei] + 175)
                .attr('x', settings.labelWidth - 20)
                .attr('class', 'value end')
                .style('pointer-events', 'none');
            infoUpdate.selectAll('.value.end')
                .text(_expand ? `${Moment(stats.end).format(theme.date.short)}` : '');

            // fu ie
            // const parent = this.parentElement || this.parentNode;

            const getNearest = function (e, obj, offset) {
                const date = x.invert(D3pointer(e)[0] + (offset || 0));
                let idx = bisect(data.timeline, date);
                if (idx >= data.timeline.length) {
                    idx = data.timeline.length - 1;
                }
                let d = data.timeline[idx];
                // snap to the closest one
                if (idx > 0) {
                    const d0 = data.timeline[idx - 1];
                    if ((date - xval(d0)) < (xval(d) - date)) {
                        d = d0;
                    }
                }
                return d;
            };

            // D3select(parent)
            svg
                .attr('pointer-events', 'all')
                // .on('mouseout', mouseout)
                .on('mouseenter', mouseenter)
                .on('click', function (e) {
                    // snap line to nearest value
                    const d = getNearest(e, svg.node(), 0);
                    // chart might be empty
                    if (!d) return;

                    // the whole thing is being transformed (0, 15)
                    const y = D3pointer(e)[1] - 15;
                    const mi = Math.max(D3bisectLeft(positions, y) - 1, 0);
                    const metric = data.metrics[D3min([data.metrics.length - 1, mi])];
                    dispatch.call('click', my, d, metric.key, data.key);
                    e.stopPropagation();
                })
                .on('mousemove', mouseover);

            function mouseout () {
                svg.selectAll('.group-hover path').style('opacity', '0');
                svg.selectAll('.tip-container circle').style('opacity', '0');
                svg.selectAll('.tip-container text').style('opacity', '0');
                svg.selectAll('.tip-container rect').style('fill-opacity', '0');
                svg.selectAll('.tip-container rect').style('stroke-opacity', '0');
                svg.selectAll('.scale-slider').style('opacity', '0');
            }

            function mouseenter () {
                svg.selectAll('.group-hover path').style('opacity', '1');
                svg.selectAll('.tip-container circle').style('opacity', '1');
                svg.selectAll('.tip-container text').style('opacity', '1');
                svg.selectAll('.tip-container rect').style('fill-opacity', '1');
                svg.selectAll('.tip-container rect').style('stroke-opacity', '1');
                svg.selectAll('.scale-slider').style('opacity', '1');
            }

            function mouseover (e) {
                // snap line to nearest value
                const d = getNearest(e, svg.node(), 0);
                // chart might be empty
                if (!d) {
                    return;
                }
                // defense against the dark arts
                svg.selectAll('.group-hover path').style('opacity', '1');
                svg.selectAll('.tip-container circle').style('opacity', '1');
                svg.selectAll('.tip-container text').style('opacity', '1');
                svg.selectAll('.tip-container rect').style('fill-opacity', '1');
                svg.selectAll('.tip-container rect').style('stroke-opacity', '1');
                svg.selectAll('.scale-slider').style('opacity', '1');

                const ddate = xval(d);

                const posX = x(ddate);
                svg.selectAll('.group-hover path')
                    .attr('class', key)
                    .attr('d', function () {
                        return 'M' +
                        (posX) + ',' + 0 + ' ' +
                        (posX) + ',' + (totalheight + 10); // let the hover line hit the x-axis
                    });

                svg.selectAll('.tip-container')
                    .attr('transform', function (graph, i) {
                        const metric = localKey.get(this);
                        const y = localY.get(this);
                        const text = D3select(this).select('.tip-text').text(null);
                        const circle = D3select(this).select('.tip-circle');
                        const rect = D3select(this).select('.tip-rect');
                        const date = D3select(this).select('.tip-date');

                        date.text(Moment(ddate).format(theme.date.long));

                        let ttext = 'no value';
                        if (metric && metric.value(d) !== undefined && metric.value(d) != null) {
                            ttext = metric.tipFormat ? metric.tipFormat(metric.value(d), d) : D3format('~s')(metric.value(d));
                        }
                        if (!Array.isArray(ttext)) {
                            ttext = [[ttext]];
                        }

                        if (text.node()) {
                            ttext.forEach((v, i) => AddValueLine(text, v, i, theme));
                        }

                        text.classed('bad', (metric && metric.bad) ? metric.bad(d) : false);

                        // adjust tip rect width
                        let rwidth = 0;
                        if (text.node()) {
                            rwidth = text.node().getBBox().width + 10;
                            const h = text.node().getBBox().height + 5;
                            const w = text.node().getBBox().width + 10;
                            rect.attr('width', w)
                                .attr('height', h);
                        }
                        // rect.attr('width', rwidth);

                        // scoot tooltips over if they're too close to the edge
                        if ((posX + 90) > width) {
                            text.attr('transform', 'translate(-' + 10 + ', -5)');
                            rect.attr('transform', 'translate(-' + (rwidth + 10) + ', 0)');
                            text.attr('text-anchor', 'end');
                            date.attr('text-anchor', 'end');
                        } else {
                            text.attr('transform', 'translate(10, -5)');
                            rect.attr('transform', 'translate(0, 0)');
                            date.attr('text-anchor', 'start');
                            text.attr('text-anchor', 'start');
                        }

                        // only the little circle goes up and down so text doesn't overlap
                        let posY = settings.chartHeight;
                        if (y && metric) posY = y(metric.value(d)) || settings.chartHeight;
                        if (!isFinite(posY)) {
                            posY = settings.chartHeight;
                        }
                        circle.attr('transform', `translate(0,${posY})`);
                        return `translate(${posX},0)`;
                    });
            }
        });
    }

    // getters/setters
    my.width = function (v) {
        if (!arguments.length) return width;
        width = v;
        return my;
    };

    my.height = function (v) {
        if (!arguments.length) return height;
        height = v;
        return my;
    };

    my.settings = function (v) {
        if (!arguments.length) return settings;
        settings = { ...settings, ...v };
        return my;
    };

    my.start = function (v) {
        if (!arguments.length) return start;
        start = v;
        return my;
    };

    my.end = function (v) {
        if (!arguments.length) return end;
        end = v;
        return my;
    };

    my.xval = function (v) {
        if (!arguments.length) return xval;
        xval = v;
        return my;
    };

    my.colors = function (v) {
        if (!arguments.length) return colors;
        colors = v;
        return my;
    };

    my.colorScale = function (v) {
        if (!arguments.length) return colorScale;
        colorScale = v;
        return my;
    };

    my.theme = function (v) {
        if (!arguments.length) return theme;
        theme = v || theme;
        return my;
    };

    my.expand = function (v) {
        if (!arguments.length) return expand;
        expand = v;
        return my;
    };

    my.txfm = function (v) {
        if (!arguments.length) return txfm;
        txfm = v;
        return my;
    };

    my.on = function () {
        const value = dispatch.on.apply(dispatch, arguments);
        return value === dispatch ? my : value;
    };

    return my;
};

export default D3SparklineGroup;
